import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  DEFAULT_ZIPCODE,
  INTELLIMIZE_PRICESECTION_EVENT,
  ONLY_NUMBER_REGEX
} from '../../constants/defaults';
import Zipcode from '../common/zipcode';
import Paginator from './Paginator/index.jsx';
import PriceCard from './PriceCard/index.jsx';
import { priceData } from './data';
import { fetchPrices } from '../../services/Api/Pricing/dtcPrcing';
import _ from 'lodash';
import './index.scss';

const PAGE_SIZE_DESKTOP = 5;
const PAGE_SIZE_TABLET = 3;
const PAGE_SIZE_MOBILE = 2;
const CARD_WIDTH = 240;
const CARD_GAP = 16;
const PREVIEW_MIN_LEFTOVER = 40;

const PriceSection = () => {
  const getPageSize = () => {
    if (typeof window === 'undefined') return PAGE_SIZE_DESKTOP;
    if (window.innerWidth <= 600) return PAGE_SIZE_MOBILE;
    if (window.innerWidth <= 1024) return PAGE_SIZE_TABLET;
    if (window.innerWidth <= 1440) return PAGE_SIZE_DESKTOP;
    return PAGE_SIZE_DESKTOP;
  };

  const [drugList, setDrugList] = useState(priceData);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(getPageSize());
  const [focusedGlobalIndex, setFocusedGlobalIndex] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const viewportRef = useRef(null);

  const zipcodeValue =
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('zipCodeData') &&
    JSON.parse(localStorage.getItem('zipCodeData'));
  const locationZip =
    zipcodeValue && ONLY_NUMBER_REGEX.test(zipcodeValue.zipCode)
      ? zipcodeValue.zipCode
      : `${zipcodeValue?.city}, ${zipcodeValue?.state}`;
  const [zipCode, setZipCode] = useState(locationZip || DEFAULT_ZIPCODE);

  const totalPages = Math.ceil(drugList.length / pageSize);
  const clampPage = useCallback(
    p => Math.min(Math.max(p, 0), Math.max(totalPages - 1, 0)),
    [totalPages]
  );

  const startIdx = page * pageSize;
  const endIdx = startIdx + pageSize - 1;
  const nextPageHasItems = startIdx + pageSize < drugList.length;
  const visibleItems = drugList.slice(startIdx, startIdx + pageSize);
  const itemsToRender =
    showPreview && nextPageHasItems
      ? [...visibleItems, drugList[startIdx + pageSize]]
      : visibleItems;

  const getDrugPrices = async () => {
    setLoading(true);
    const MAX_BATCH = 10;
    const chunk = (arr, size) =>
      Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
        arr.slice(i * size, i * size + size)
      );

    const drugs = drugList.map(drug => ({
      ndc: drug.ndc,
      quantity: drug.quantity
    }));

    let allPrices = [];
    const batches = chunk(drugs, MAX_BATCH);

    for (const batch of batches) {
      try {
        const batchResp = await fetchPrices(batch, zipCode);
        if (!_.isEmpty(batchResp)) {
          allPrices = allPrices.concat(batchResp);
        }
      } catch (e) {
        console.log('ERROR fetching batch prices', e);
      }
    }

    if (!_.isEmpty(allPrices)) {
      drugList.forEach(drug => {
        const matchingPrice = allPrices.find(price => price.ndc === drug.ndc);
        if (matchingPrice) {
          drug.price = matchingPrice.price;
          drug.loyaltyPrice = matchingPrice.loyaltyPrice;
        }
      });

      setDrugList([...drugList]);
      setLoading(false);
      return allPrices;
    }
  };

  const handleZipCodeUpdate = (z = '') => {
    setZipCode(z);
  };

  const handleCardKeyDown = e => {
    switch (e.key) {
      case 'ArrowRight': {
        if (page < totalPages - 1) {
          const newPage = page + 1;
          goTo(newPage);
          focusGlobalIndex(newPage * pageSize);
        }
        e.preventDefault();
        break;
      }
      case 'ArrowLeft': {
        if (page > 0) {
          const newPage = page - 1;
          goTo(newPage);
          focusGlobalIndex(newPage * pageSize);
        }
        e.preventDefault();
        break;
      }
      case 'Home': {
        goTo(0);
        focusGlobalIndex(0);
        e.preventDefault();
        break;
      }
      case 'End': {
        const lastIdx = drugList.length - 1;
        goTo(totalPages - 1);
        focusGlobalIndex(lastIdx - (lastIdx % pageSize));
        e.preventDefault();
        break;
      }
      default:
        break;
    }
  };

  const onLocationClick = () => {
    _.sendIntellimizeEvent(INTELLIMIZE_PRICESECTION_EVENT.LOCATION_CLICK);
  };

  const goTo = p => setPage(clampPage(p));
  const prevPage = () => goTo(page - 1);
  const nextPage = () => goTo(page + 1);

  const focusGlobalIndex = idx => {
    setFocusedGlobalIndex(idx);
  };

  const getCardSizes = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 599;
    if (isMobile) {
      return {
        cardWidth: 160,
        previewMinLeftover: 20,
        cardGap: CARD_GAP
      };
    }
    return {
      cardWidth: CARD_WIDTH,
      previewMinLeftover: PREVIEW_MIN_LEFTOVER,
      cardGap: CARD_GAP
    };
  };

  const computePreview = useCallback(() => {
    if (!viewportRef.current) return setShowPreview(false);
    const hasNextPage = startIdx + pageSize < drugList.length;
    if (!hasNextPage) return setShowPreview(false);
    const viewportWidth = viewportRef.current.clientWidth;
    const { cardWidth, previewMinLeftover, cardGap } = getCardSizes();
    const occupied = pageSize * cardWidth + (pageSize - 1) * cardGap;
    const leftover = viewportWidth - occupied;
    setShowPreview(leftover >= previewMinLeftover);
  }, [pageSize, startIdx, drugList.length]);

  useEffect(() => {
    computePreview();
  }, [computePreview, pageSize, page]);

  useEffect(() => {
    const onResize = () => {
      setPageSize(getPageSize());
      requestAnimationFrame(() => computePreview());
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [computePreview]);

  useEffect(() => {
    getDrugPrices();
  }, [zipCode]);

  useEffect(() => {
    const firstCard = viewportRef.current?.querySelector(
      '.price-card[tabindex="0"]'
    );
    firstCard && firstCard.focus();
  }, [page, pageSize]);

  useEffect(() => {
    if (
      focusedGlobalIndex >= startIdx &&
      focusedGlobalIndex <= endIdx &&
      viewportRef.current
    ) {
      const el = viewportRef.current.querySelector(
        `.price-card[data-index="${focusedGlobalIndex}"]`
      );
      el && el.focus();
    }
  }, [page, pageSize, focusedGlobalIndex, startIdx, endIdx]);

  return (
    <section
      id="price-section-home"
      className="price-section price-section-abtest"
      aria-label="Affordable prices on everyday prescriptions"
    >
      <div className="price-section__headingRow">
        <h2 className="price-section__title">
          Affordable prices on everyday prescriptions
        </h2>
        <div
          className="price-section__location"
          aria-label="Location"
          onClick={() => onLocationClick()}
        >
          <div className="topZipcode">
            <Zipcode
              zipCode={zipCode}
              handleZipCodeChange={handleZipCodeUpdate}
            />
          </div>
        </div>
      </div>

      <div
        className={`price-section__viewport ${
          showPreview ? 'has-preview' : ''
        }`}
        ref={viewportRef}
        role="group"
        aria-roledescription="carousel"
        aria-label="Price cards carousel"
      >
        <ul className="price-section__track">
          {itemsToRender.map((item, i) => {
            const globalIndex = startIdx + i;
            const isPreview =
              showPreview && i === itemsToRender.length - 1 && nextPageHasItems;
            return (
              <PriceCard
                key={item.ndc || globalIndex}
                item={item}
                globalIndex={globalIndex}
                isFocused={!isPreview && globalIndex === focusedGlobalIndex}
                loading={loading}
                onKeyDown={isPreview ? undefined : handleCardKeyDown}
                isPreview={isPreview}
                aria-hidden={isPreview ? 'true' : undefined}
                tabIndex={isPreview ? -1 : undefined}
              />
            );
          })}
        </ul>
      </div>

      <Paginator
        page={page}
        totalPages={totalPages}
        onPrev={prevPage}
        onNext={nextPage}
        onGoTo={goTo}
      />
    </section>
  );
};

export default PriceSection;
